import { isDefined } from "@clipboard-health/util-ts";
import { type SxProps, type Theme } from "@mui/material";
import {
  mergeSxProps,
  Text,
  type TextProps,
  TextSkeleton,
} from "@src/appV2/redesign/system/ui-components";

export interface HourlyPayLabelProps {
  /**
   * The formatted hourly pay for the shift.
   */
  hourlyPay: string | undefined;
  /**
   * Whether the pay data is being fetched.
   */
  isLoading?: boolean;
  /**
   * Custom styles to apply to the component.
   */
  sx?: SxProps<Theme>;
  /**
   * The size of the label.
   * Large corresponds to the h4 size for label and body1 for the /hr.
   * Medium corresponds to the h6 size for label and caption for the /hr.
   */
  size?: "large" | "medium" | "small";
}

const sizeToVariant: Record<NonNullable<HourlyPayLabelProps["size"]>, TextProps["variant"]> = {
  large: "h4",
  medium: "h6",
  small: "body1",
};

// TODO: Move to cbh-core as fast follow up to https://linear.app/clipboardhealth/issue/SPRTA-1362/update-shift-details-bottom-sheet
export function HourlyPayLabel(props: HourlyPayLabelProps) {
  const { hourlyPay, isLoading, sx, size = "medium" } = props;

  const payLabelVariant = sizeToVariant[size];

  if (isLoading) {
    return <TextSkeleton variant={payLabelVariant} width="7ch" sx={sx} />;
  }

  if (!isDefined(hourlyPay)) {
    return null;
  }

  return (
    <Text semibold variant={payLabelVariant} sx={mergeSxProps({ whiteSpace: "nowrap" }, sx)}>
      {hourlyPay}{" "}
      <Text semibold inline variant={payLabelVariant}>
        /hr
      </Text>
    </Text>
  );
}
